/**
 * Export Panel component
 */

import * as React from 'react';
import {
  Button,
  Card,
  CardContent,
  Progress,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  cn,
} from '@frameit/ui';
import { useProjectStore, useSelectedArtwork, getLocalImageUrl } from '../../stores/projectStore';
import { useAuthStore } from '../../stores/authStore';
import { renderExport, type ExportProgress } from './exportRenderer';

export interface ExportPanelProps {
  /** Custom class name */
  className?: string;
}

type ExportStep = 'idle' | 'preparing' | 'rendering' | 'encoding' | 'finalizing' | 'done' | 'error';

/** Export panel component */
export function ExportPanel({ className }: ExportPanelProps) {
  const project = useProjectStore((s) => s.project);
  const selectedArtwork = useSelectedArtwork();
  const canExport = useAuthStore((s) => s.canExport());
  const canVideoExport = useAuthStore((s) => s.canUseVideoExport());
  const requiresWatermark = useAuthStore((s) => s.requiresWatermark());
  const tierLimits = useAuthStore((s) => s.getTierLimits());

  const [showDialog, setShowDialog] = React.useState(false);
  const [exportType, setExportType] = React.useState<'image' | 'video'>('image');
  const [format, setFormat] = React.useState<'png' | 'jpg' | 'webp'>('png');
  const [quality, setQuality] = React.useState(92);
  const [resolution, setResolution] = React.useState(tierLimits.maxExportResolution);
  const [step, setStep] = React.useState<ExportStep>('idle');
  const [progress, setProgress] = React.useState(0);
  const [error, setError] = React.useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);

  // Handle export
  const handleExport = async () => {
    if (!project || !selectedArtwork || !canExport) return;

    setStep('preparing');
    setProgress(0);
    setError(null);

    try {
      // Get artwork image URL
      const artworkImageUrl = getLocalImageUrl(selectedArtwork.artworkId);
      if (!artworkImageUrl) {
        throw new Error('Artwork image not found');
      }

      // Progress callback
      const onProgress = (info: ExportProgress) => {
        setStep(info.step);
        setProgress(info.progress * 100);
      };

      // Render export
      const blob = await renderExport(
        selectedArtwork,
        artworkImageUrl,
        project.scene,
        {
          format,
          quality: quality / 100,
          maxDimension: resolution,
          watermark: requiresWatermark,
        },
        onProgress
      );

      setStep('done');

      // Create download URL
      const url = URL.createObjectURL(blob);
      setDownloadUrl(url);
    } catch (e) {
      setStep('error');
      setError((e as Error).message);
    }
  };

  // Download file
  const handleDownload = () => {
    if (!downloadUrl) return;

    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `${project?.name || 'frameit-export'}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Reset dialog
  const handleClose = () => {
    setShowDialog(false);
    setStep('idle');
    setProgress(0);
    setError(null);
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
      setDownloadUrl(null);
    }
  };

  return (
    <div className={cn('p-4 space-y-4', className)}>
      <h3 className="font-semibold">Export</h3>

      {!canExport && (
        <Card className="bg-amber-500/10 border-amber-500/20">
          <CardContent className="p-3 text-sm">
            <p className="font-medium text-amber-700 dark:text-amber-400">
              Export Limit Reached
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Upgrade to Pro for unlimited exports
            </p>
            <Button size="sm" className="mt-2">
              Upgrade
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Export options */}
      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Export type */}
          <div>
            <label className="text-sm font-medium">Export Type</label>
            <div className="flex gap-2 mt-2">
              <Button
                variant={exportType === 'image' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setExportType('image')}
              >
                Image
              </Button>
              <Button
                variant={exportType === 'video' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setExportType('video')}
                disabled={!canVideoExport}
              >
                Video {!canVideoExport && '(Pro)'}
              </Button>
            </div>
          </div>

          {/* Format */}
          {exportType === 'image' && (
            <div>
              <label className="text-sm font-medium">Format</label>
              <div className="flex gap-2 mt-2">
                {(['png', 'jpg', 'webp'] as const).map((f) => (
                  <Button
                    key={f}
                    variant={format === f ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormat(f)}
                  >
                    {f.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Quality (for jpg/webp) */}
          {exportType === 'image' && format !== 'png' && (
            <div>
              <label className="text-sm font-medium">Quality: {quality}%</label>
              <input
                type="range"
                min={50}
                max={100}
                value={quality}
                onChange={(e) => setQuality(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
          )}

          {/* Resolution */}
          <div>
            <label className="text-sm font-medium">
              Max Resolution: {resolution}px
            </label>
            <input
              type="range"
              min={1024}
              max={tierLimits.maxExportResolution}
              step={512}
              value={resolution}
              onChange={(e) => setResolution(parseInt(e.target.value))}
              className="w-full mt-2"
            />
            {resolution > 2048 && requiresWatermark && (
              <p className="text-xs text-muted-foreground mt-1">
                Free tier exports include watermark
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Export button */}
      <Button
        className="w-full"
        disabled={!project || !selectedArtwork || !canExport}
        onClick={() => {
          setShowDialog(true);
          handleExport();
        }}
      >
        Export {exportType === 'image' ? 'Image' : 'Video'}
      </Button>

      {!selectedArtwork && project && (
        <p className="text-xs text-muted-foreground text-center">
          Select an artwork to export
        </p>
      )}

      {/* Export dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {step === 'done' ? 'Export Complete' : 'Exporting...'}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            {step === 'idle' && (
              <p className="text-muted-foreground">Preparing export...</p>
            )}

            {['preparing', 'rendering', 'encoding', 'finalizing'].includes(step) && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="capitalize">{step}</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {step === 'done' && (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-green-500/10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p>Your export is ready!</p>
              </div>
            )}

            {step === 'error' && (
              <div className="text-center space-y-2">
                <div className="w-16 h-16 mx-auto bg-red-500/10 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                <p className="text-red-500">{error}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            {step === 'done' && (
              <>
                <Button variant="outline" onClick={handleClose}>
                  Close
                </Button>
                <Button onClick={handleDownload}>
                  Download
                </Button>
              </>
            )}
            {step === 'error' && (
              <>
                <Button variant="outline" onClick={handleClose}>
                  Close
                </Button>
                <Button onClick={handleExport}>
                  Retry
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
